import type { ChatEntryWithSubTables } from "../../shared/aiSdkMessages";
import type { StreamEvent } from "../../shared/streamEvents";
import { emitSpan } from "./tracing";

type ContentType = "assistant" | "reasoning" | "tool";
type Phase = "sse_start" | "sse_content" | "zero_row" | "rendered";
type Entry = Pick<
  ChatEntryWithSubTables,
  "id" | "previousEntryId" | "entryType"
>;
type Send = { started: number; branchId: string; emitted: Set<string> };
type Message = {
  receivedAt: number;
  userEntryId?: string;
  contentType: ContentType;
  phases: Partial<Record<Phase, number>>;
};

const MAX_SENDS = 20;
const MAX_MESSAGES = 200;
const RETENTION_MS = 10 * 60 * 1_000;

export function createChatLatencyRecorder({
  now = () => performance.now(),
  emit = emitSpan,
}: {
  now?: () => number;
  emit?: typeof emitSpan;
} = {}) {
  const sends = new Map<string, Send>();
  const messages = new Map<string, Message>();
  const unclaimedRenders = new Map<string, number>();

  function prune() {
    const cutoff = now() - RETENTION_MS;
    for (const [id, send] of sends) if (send.started < cutoff) sends.delete(id);
    for (const [id, message] of messages)
      if (
        message.receivedAt < cutoff ||
        (message.userEntryId && !sends.has(message.userEntryId))
      )
        messages.delete(id);
    while (sends.size > MAX_SENDS) sends.delete(sends.keys().next().value!);
    while (messages.size > MAX_MESSAGES)
      messages.delete(messages.keys().next().value!);
    while (unclaimedRenders.size > MAX_MESSAGES)
      unclaimedRenders.delete(unclaimedRenders.keys().next().value!);
  }

  function flush(messageId: string, message: Message) {
    if (!message.userEntryId) return;
    const send = sends.get(message.userEntryId);
    if (!send) return;
    for (const [phase, at] of Object.entries(message.phases)) {
      const key = `${phase}:${message.contentType}`;
      if (at < send.started || send.emitted.has(key)) continue;
      send.emitted.add(key);
      emit(
        "ui.chat.latency",
        {
          "chat.user_entry_id": message.userEntryId,
          "chat.message_id": messageId,
          "branch.id": send.branchId,
          "chat.phase": phase,
          "chat.content_type": message.contentType,
          "chat.elapsed_ms": at - send.started,
        },
        at - send.started,
        performance.timeOrigin + at
      );
    }
  }

  return {
    sent(userEntryId: string, branchId: string) {
      const started = now();
      sends.set(userEntryId, { started, branchId, emitted: new Set() });
      prune();
      emit(
        "ui.chat.latency",
        {
          "chat.user_entry_id": userEntryId,
          "branch.id": branchId,
          "chat.phase": "send",
          "chat.elapsed_ms": 0,
        },
        0,
        performance.timeOrigin + started
      );
    },
    failed(userEntryId: string) {
      sends.delete(userEntryId);
      prune();
    },
    received(event: StreamEvent) {
      if (!sends.size || !("messageId" in event)) return;
      prune();
      let message = messages.get(event.messageId);
      if (event.type === "message-start") {
        message ??= {
          receivedAt: now(),
          contentType: event.role,
          phases: {},
        };
        message.phases.sse_start ??= now();
        messages.set(event.messageId, message);
      } else if (
        message &&
        ((event.type === "message-content" && event.delta.trim()) ||
          (event.type === "tool-call-preview" &&
            event.input != null &&
            typeof event.input === "object" &&
            Object.keys(event.input).length > 0))
      ) {
        message.phases.sse_content ??= now();
      }
      if (message) flush(event.messageId, message);
    },
    observed(entries: readonly Entry[]) {
      if (!sends.size) return;
      prune();
      const owners = new Map<string, string>();
      for (const entry of entries) {
        const userEntryId =
          entry.entryType === "UserMessage"
            ? sends.has(entry.id)
              ? entry.id
              : undefined
            : entry.previousEntryId
              ? owners.get(entry.previousEntryId)
              : undefined;
        if (!userEntryId) continue;
        owners.set(entry.id, userEntryId);
        const contentType =
          entry.entryType === "AssistantMessage"
            ? "assistant"
            : entry.entryType === "AssistantReasoning"
              ? "reasoning"
              : entry.entryType === "AssistantToolUse"
                ? "tool"
                : null;
        if (!contentType) continue;
        const message = messages.get(entry.id) ?? {
          receivedAt: now(),
          contentType,
          phases: {},
        };
        message.userEntryId = userEntryId;
        message.phases.zero_row ??= now();
        const renderedAt = unclaimedRenders.get(entry.id);
        if (renderedAt !== undefined) {
          unclaimedRenders.delete(entry.id);
          message.phases.rendered ??= renderedAt;
        }
        messages.set(entry.id, message);
        flush(entry.id, message);
      }
    },
    rendered(messageId: string) {
      const message = messages.get(messageId);
      if (!message) {
        if (sends.size) unclaimedRenders.set(messageId, now());
        return;
      }
      message.phases.rendered ??= now();
      flush(messageId, message);
    },
  };
}

export const chatLatency = createChatLatencyRecorder();
